import { clamp } from '../../shared/scalar';
import { damageCore, damagePlayer, playerById } from '../damage';
import { healPlayer, hurtEnemy, knockBack } from '../effects';
import { type Obstacles, touchesObstacle } from '../obstacles';
import type { PlayerId, PlayerState, ProjectileState, SimState } from '../state';
import type { StepContext } from './types';

const LANDING_KNOCKBACK_PER_RADIUS = 0.25;
const RETURN_TICKS = 600;

export function projectiles({
  state,
  content,
  enemyGrid,
  obstacles,
  enemyObstacles,
}: StepContext): void {
  enemyGrid.rebuild(state.enemies);
  const { width, height } = state.arena;
  let kept = 0;
  for (const projectile of state.projectiles) {
    if (projectile.returning === true && flewHome(state, projectile)) {
      continue;
    }
    projectile.x += projectile.vx;
    projectile.y += projectile.vy;
    projectile.ticksLeft -= 1;
    let spent = false;
    if (projectile.owner.kind === 'enemy') {
      spent = hitPlayerOrCore(state, projectile);
    } else if (projectile.returning !== true && projectile.arc === undefined) {
      spent = hitEnemies(state, enemyGrid, projectile, enemyObstacles);
    }
    const held = turnsAround(projectile, content);
    const blocked =
      !spent &&
      projectile.arc === undefined &&
      projectile.returning !== true &&
      !held &&
      touchesObstacle(projectile, obstacles);
    const inArena =
      held ||
      (projectile.x >= 0 && projectile.x <= width && projectile.y >= 0 && projectile.y <= height);
    if (!spent && (blocked || projectile.ticksLeft <= 0 || !inArena)) {
      spent = !fliesOn(state, enemyGrid, projectile, enemyObstacles);
    }
    if (!spent) {
      state.projectiles[kept] = projectile;
      kept += 1;
    }
  }
  state.projectiles.length = kept;
}

// A body that turns around its player passes over the obstacles and past the edge of the arena,
// as it is held, not thrown.
function turnsAround(projectile: ProjectileState, content: StepContext['content']): boolean {
  const { owner } = projectile;
  return owner.kind === 'weapon' && content.weapons.get(owner.weaponId)?.effect.kind === 'orbit';
}

function fliesOn(
  state: SimState,
  grid: StepContext['enemyGrid'],
  projectile: ProjectileState,
  obstacles: Obstacles,
): boolean {
  if (projectile.arc !== undefined) {
    land(state, grid, projectile, obstacles);
    return false;
  }
  if (projectile.returnTo === undefined || projectile.returning === true) {
    return false;
  }
  const ally = mostInjured(state);
  if (ally === undefined) {
    return false;
  }
  projectile.returnTo = ally.id;
  projectile.returning = true;
  projectile.ticksLeft = RETURN_TICKS;
  projectile.x = clamp(projectile.x, 0, state.arena.width);
  projectile.y = clamp(projectile.y, 0, state.arena.height);
  return true;
}

function land(
  state: SimState,
  grid: StepContext['enemyGrid'],
  projectile: ProjectileState,
  obstacles: Obstacles,
): void {
  const byPlayer = creditedPlayer(state, projectile);
  const found = grid.query(projectile.x, projectile.y, projectile.radius);
  for (let i = 0; i < found; i++) {
    const enemy = state.enemies[grid.result(i)];
    if (enemy === undefined || enemy.hp <= 0) {
      continue;
    }
    hurtEnemy(state, enemy, projectile.damage, byPlayer, projectile);
    knockBack(enemy, projectile, projectile.radius * LANDING_KNOCKBACK_PER_RADIUS, obstacles);
  }
}

function mostInjured(state: SimState): PlayerState | undefined {
  let worst: PlayerState | undefined;
  for (const player of state.players) {
    if (
      !player.downed &&
      (worst === undefined || player.hp / player.maxHp < worst.hp / worst.maxHp)
    ) {
      worst = player;
    }
  }
  return worst;
}

function flewHome(state: SimState, projectile: ProjectileState): boolean {
  const ally =
    projectile.returnTo === undefined ? undefined : playerById(state, projectile.returnTo);
  if (ally === undefined || ally.downed) {
    return true;
  }
  const dx = ally.x - projectile.x;
  const dy = ally.y - projectile.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const speed = Math.sqrt(projectile.vx * projectile.vx + projectile.vy * projectile.vy);
  if (distance <= speed + ally.radius) {
    healPlayer(state, ally, projectile.heal ?? 0);
    if (projectile.owner.kind === 'weapon') {
      state.events.push({
        type: 'weaponCaught',
        playerId: ally.id,
        weaponId: projectile.owner.weaponId,
        x: ally.x,
        y: ally.y,
      });
    }
    return true;
  }
  projectile.vx = (dx / distance) * speed;
  projectile.vy = (dy / distance) * speed;
  return false;
}

function hitEnemies(
  state: SimState,
  grid: StepContext['enemyGrid'],
  projectile: ProjectileState,
  obstacles: Obstacles,
): boolean {
  const byPlayer = creditedPlayer(state, projectile);
  const found = grid.query(projectile.x, projectile.y, projectile.radius);
  for (let i = 0; i < found; i++) {
    const enemy = state.enemies[grid.result(i)];
    if (enemy === undefined || enemy.hp <= 0 || projectile.hitIds?.includes(enemy.id) === true) {
      continue;
    }
    hurtEnemy(state, enemy, projectile.damage, byPlayer, {
      x: projectile.prevX,
      y: projectile.prevY,
    });
    if (projectile.owner.kind === 'weapon' && projectile.damage > 0) {
      const { playerId, weaponId } = projectile.owner;
      state.events.push({
        type: 'weaponHit',
        playerId,
        weaponId,
        id: enemy.id,
        x: enemy.x,
        y: enemy.y,
      });
    }
    if (projectile.knockback !== undefined) {
      knockBack(enemy, projectile, projectile.knockback, obstacles);
    }
    if (projectile.pierceLeft <= 0) {
      return true;
    }
    projectile.pierceLeft -= 1;
    (projectile.hitIds ??= []).push(enemy.id);
  }
  return false;
}

function creditedPlayer(state: SimState, projectile: ProjectileState): PlayerId | null {
  const { owner } = projectile;
  switch (owner.kind) {
    case 'player':
    case 'weapon':
      return owner.playerId;
    case 'trap':
      return state.traps.find((trap) => trap.id === owner.trapId)?.ownerId ?? null;
    case 'enemy':
      return null;
  }
}

function hitPlayerOrCore(state: SimState, projectile: ProjectileState): boolean {
  for (const player of state.players) {
    if (!player.downed && touches(projectile, player)) {
      damagePlayer(state, player, projectile.damage);
      return true;
    }
  }
  if (touches(projectile, state.core)) {
    damageCore(state, projectile.damage);
    return true;
  }
  return false;
}

function touches(
  projectile: ProjectileState,
  circle: { x: number; y: number; radius: number },
): boolean {
  const dx = projectile.x - circle.x;
  const dy = projectile.y - circle.y;
  const reach = projectile.radius + circle.radius;
  return dx * dx + dy * dy <= reach * reach;
}
